import { SwiperSlide } from 'swiper/react';
import dayjs from 'dayjs';
import React, { useState } from 'react';

import {
    Carousel,
    CarouselArrowNavigation,
    CarouselWrapper
} from '$partials/base/carousel';

import {
    Footer,
    Header
} from '$partials/global';

import { ArticleListingItem } from '$partials/items';
import { Button } from '$partials/ui';
import { ModuleDispatcher } from '$partials/modules';
import { SimpleImage } from '$partials/media';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').MagazineListingPage}
 */
const MagazineListingPage = (props) => {
    const [activeTab, setActiveTab] = useState(0);

    const page = props.data.page;

    const issues = page.items?.edges;

    return (
        <>
            <Header
                {...props?.data?.header}
                pageType={page.__typename} />

            <main id='main-content'>
                <div className='layout-container layout-site-outer-padding pb-8 md:pb-16'>
                    <section className='col-wrapper-breakout bg-surface-panel-blue rounded-lg pt-24 pb-16'>
                        <div className='col-padding'>
                            <h1 className='type-style-sans-display'>
                                {page.title}
                            </h1>

                            {page.listingStandfirst && (
                                <h2 className='type-style-sans-h3 mt-8 w-full sm:w-9/12 md:w-1/2'>
                                    {page.listingStandfirst}
                                </h2>
                            )}
                        </div>
                    </section>

                    <section className='col-wrapper-breakout bg-surface-panel-blue rounded-lg mt-8 md:mt-16'>
                        <div
                            aria-label='Issue tabs'
                            className='col-padding py-24'
                            role='tablist'>
                            <p className='type-style-sans-body mb-16'>
                                Latest issues
                            </p>

                            <div
                                className='gap-8 flex overflow-y-scroll'
                                role='tablist'>
                                {issues?.map((node, idx) => {
                                    const issue = node.node;
                                    return (
                                        <Button
                                            aria-controls={`panel-${idx}`}
                                            as='button'
                                            className={`${activeTab === idx ? 'button-primary' : 'button-outcome'} whitespace-nowrap`}
                                            id={`tab-${idx}`}
                                            key={issue.id}
                                            onClick={setActiveTab.bind(null, idx)}
                                            tab-index={activeTab === idx ? 0 : -1}>
                                            {issue.issueText}{dayjs(issue.publicationDate).format(' YYYY')}
                                        </Button>
                                    );}
                                )}
                            </div>
                        </div>

                        <div className='-mx-4 sm:-mx-4 md:-mx-8 border-t border-border-default' />

                        <div className=''>
                            {issues?.map((node, idx) => {
                                const issue = node.node;

                                return (
                                    <div
                                        aria-labelledby={`tab-${idx}`}
                                        className={`${activeTab === idx ? 'flex-col sm:flex-row flex' : ''}`}
                                        hidden={activeTab !== idx}
                                        id={`panel-${idx}`}
                                        key={issue.id}
                                        role='tabpanel'>
                                        <div className='w-full sm:w-3/12 px-4 md:px-8 py-8 md:py-16'>
                                            <figure className='rounded-lg overflow-hidden'>
                                                <SimpleImage {...issue.cover?.image}/>
                                            </figure>
                                        </div>

                                        <div className='sm:border-l border-border-default w-full sm:w-9/12 col-padding py-8 md:py-16'>
                                            <p className='type-style-sans-body'>
                                                {issue.issueText}{dayjs(issue.publicationDate).format(', YYYY')}
                                            </p>

                                            <p className='type-style-sans-h2-med mt-8'>
                                                {issue.title}
                                            </p>

                                            <div
                                                className='rich-text w-full sm:w-8/9 md:w-6/9 mt-8'
                                                dangerouslySetInnerHTML={{ __html: issue.text }} />

                                            {issue.relatedArticles?.length > 0 && (
                                                <section className='mt-32 -mx-16'>
                                                    <Carousel
                                                        swiperConfig={{
                                                            a11y: { enabled: true },
                                                            slidesPerView: 1.2,
                                                            slidesOffsetBefore: 8,
                                                            slidesOffsetAfter: 8,
                                                            breakpoints: {
                                                                922: {
                                                                    slidesPerView: 1.6,
                                                                },
                                                                1440: {
                                                                    slidesPerView: 2.3,
                                                                    slidesOffsetBefore: 16,
                                                                    slidesOffsetAfter: 16
                                                                }
                                                            },
                                                            spaceBetween: 8,
                                                            grabCursor: false,
                                                        }}>
                                                        <div className='flex justify-between items-center mt-10 pl-16 mb-12'>
                                                            <p className='type-style-sans-h3-med'>
                                                                {'Articles from this issue'}
                                                            </p>

                                                            {/* Desktop */}
                                                            {issue.relatedArticles.length > 2 && (
                                                                <div className='hidden md:block'>
                                                                    <CarouselArrowNavigation />
                                                                </div>
                                                            )}

                                                            {/* Tablet */}
                                                            {issue.relatedArticles.length > 1 && (
                                                                <div className='hidden sm:block px-4 md:hidden'>
                                                                    <CarouselArrowNavigation />
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className='mx-4 sm:-mx-8 md:-mx-16 pl-4 sm:pl-16'>
                                                            <CarouselWrapper>
                                                                {issue.relatedArticles.map((item) => {
                                                                    if (!item.article) { return null; }

                                                                    const article = item.article;
                                                                    return (
                                                                        <SwiperSlide
                                                                            aria-roledescription='slide'
                                                                            className='select-none cursor-pointer flex flex-wrap mt-4'
                                                                            key={article.id}
                                                                            role='group'>
                                                                            <ArticleListingItem {...article} />
                                                                        </SwiperSlide>
                                                                    );
                                                                }).filter(Boolean)}
                                                            </CarouselWrapper>
                                                        </div>
                                                    </Carousel>
                                                </section>
                                            )}
                                        </div>
                                    </div>
                                );}
                            )}
                        </div>
                    </section>

                    {page.modules?.length > 0 && (
                        <div className='flex flex-col gap-y-8 md:gap-y-16 mt-8 md:mt-16'>
                            {page.modules.map((module, i) => (
                                <ModuleDispatcher
                                    {...module}
                                    key={i + module?.id} />
                            ))}
                        </div>
                    )}
                </div>
            </main>

            <Footer
                {...props?.data?.footer}
                pageType={page.__typename} />
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default MagazineListingPage;
